import { useCallback, useEffect, useRef } from 'react';
import chatService from 'services/chat';
import { CHAT_PAGE_SIZE } from 'utils/chat/chatConstants';
import { buildChatReconciliationPayload } from 'utils/chat/chatState';
import { ActiveThreadReconciliationIntervalMs, wait } from './useInstructorHugoChatHelpers';
import useRuntimeReconciliationBlock from './useRuntimeReconciliationBlock';

const useInstructorHugoSync = ({
    chat,
    chatRef,
    isConnected,
    isOpen,
    loadThreadSummaries,
    onMessage,
    setChat,
    setPingPayload,
}) => {
    const isRefreshingActiveThreadRef = useRef(false);
    const activeThreadRefreshRequestIdRef = useRef(0);
    const {
        isRuntimeReconciliationBlocked,
        resetRuntimeReconciliationBlock,
        setRuntimeBlocksReconciliation,
    } = useRuntimeReconciliationBlock();

    useEffect(() => {
        chatRef.current = chat;
    }, [chat, chatRef]);

    const refreshActiveThread = useCallback(
        async (chatId, { force = false, shouldIgnore = () => false, shouldReplaceChat } = {}) => {
            if (!chatId) return null;
            if (isRefreshingActiveThreadRef.current && !force) return null;

            const canReplaceChat =
                shouldReplaceChat ||
                ((currentChat, persistedChat) => {
                    return currentChat?.id === persistedChat.id;
                });
            const refreshRequestId = activeThreadRefreshRequestIdRef.current + 1;
            activeThreadRefreshRequestIdRef.current = refreshRequestId;
            isRefreshingActiveThreadRef.current = true;
            try {
                const persistedChat = await chatService.getChat(chatId, {
                    messages_top: CHAT_PAGE_SIZE,
                });
                if (!persistedChat?.id) return null;
                if (shouldIgnore()) return null;
                if (refreshRequestId !== activeThreadRefreshRequestIdRef.current) return null;

                setChat((currentChat) => {
                    if (!canReplaceChat(currentChat, persistedChat)) return currentChat;
                    return persistedChat;
                });

                return persistedChat;
            } finally {
                if (refreshRequestId === activeThreadRefreshRequestIdRef.current) {
                    isRefreshingActiveThreadRef.current = false;
                }
            }
        },
        [setChat]
    );

    const reconcileActiveThread = useCallback(
        async (chatId, options) => {
            if (isRuntimeReconciliationBlocked()) return null;
            return refreshActiveThread(chatId, options);
        },
        [isRuntimeReconciliationBlocked, refreshActiveThread]
    );

    useEffect(() => {
        if (!isOpen || !chat.id) return;

        let shouldIgnore = false;
        reconcileActiveThread(chat.id, { shouldIgnore: () => shouldIgnore }).catch(() => {
            // A later open, switch, or processing poll can reconcile the thread.
        });

        return () => {
            shouldIgnore = true;
        };
    }, [chat.id, isOpen, reconcileActiveThread]);

    useEffect(() => {
        const cleanup = setPingPayload(() => {
            if (!isOpen) return {};
            if (isRuntimeReconciliationBlocked()) return {};
            return buildChatReconciliationPayload(chatRef.current);
        });

        return cleanup;
    }, [chatRef, isOpen, isRuntimeReconciliationBlocked, setPingPayload]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.type !== 'pong' || !Array.isArray(message.chat_reconciliations)) return;

            const currentChatId = chatRef.current?.id;
            if (!currentChatId) return;

            const reconciliation = message.chat_reconciliations.find(({ chat_id }) => chat_id === currentChatId);
            if (!reconciliation?.out_of_sync) return;

            try {
                const persistedChat = await reconcileActiveThread(currentChatId);
                if (persistedChat) loadThreadSummaries();
            } catch {
                // A later ping, open, or processing refresh can reconcile the thread.
            }
        });

        return cleanup;
    }, [chatRef, loadThreadSummaries, onMessage, reconcileActiveThread]);

    useEffect(() => {
        if (!isConnected || !isOpen) return;

        const currentChatId = chatRef.current?.id;
        if (!currentChatId) return;

        reconcileActiveThread(currentChatId).catch(() => {
            // Best-effort parity with assignment chat reconciliation.
        });
    }, [chatRef, isConnected, isOpen, reconcileActiveThread]);

    useEffect(() => {
        if (!isOpen || !chat.id || !chat.is_processing) return;

        let shouldIgnore = false;
        const refreshProcessingThread = async () => {
            try {
                const persistedChat = await reconcileActiveThread(chat.id, {
                    shouldIgnore: () => shouldIgnore,
                });
                if (persistedChat && !persistedChat.is_processing) {
                    loadThreadSummaries();
                }
            } catch {
                // Keep polling while the local thread is still marked as processing.
            }
        };

        refreshProcessingThread();
        const intervalId = setInterval(refreshProcessingThread, ActiveThreadReconciliationIntervalMs);

        return () => {
            shouldIgnore = true;
            clearInterval(intervalId);
        };
    }, [chat.id, chat.is_processing, isOpen, loadThreadSummaries, reconcileActiveThread]);

    const reconcilePersistedAssistantMessage = useCallback(
        async ({ chatId, expectedRequestedInteractionId, messageId }) => {
            if (!chatId || !messageId) return;

            for (const delay of [0, 250, 750, 1500]) {
                if (delay) await wait(delay);
                try {
                    const persistedChat = await chatService.getChat(chatId, { messages_top: CHAT_PAGE_SIZE });
                    const persistedMessage = (persistedChat?.messages || []).find(({ id }) => id === messageId);
                    if (!persistedMessage) continue;
                    if (
                        expectedRequestedInteractionId &&
                        persistedMessage.requested_interaction?.id !== expectedRequestedInteractionId
                    ) {
                        continue;
                    }
                    setChat((currentChat) => {
                        if (currentChat?.id !== persistedChat.id) return currentChat;
                        return persistedChat;
                    });
                    return;
                } catch {
                    // A later retry or reopening the thread will reconcile from persisted history.
                }
            }
        },
        [setChat]
    );

    useEffect(() => {
        if (isOpen) return;
        resetRuntimeReconciliationBlock();
    }, [isOpen, resetRuntimeReconciliationBlock]);

    return {
        chatRef,
        reconcilePersistedAssistantMessage,
        refreshActiveThread,
        setRuntimeBlocksReconciliation,
    };
};

export default useInstructorHugoSync;
